<template>
  <div class="charge-discharge-chart-wrapper">
    <div ref="chartDom" class="chart-container"></div>

    <!-- 全屏对话框 -->
    <v-dialog v-model="isFullscreen" :width="dialogWidth" :height="dialogHeight" persistent class="fullscreen-dialog">
      <v-card class="fullscreen-card">
        <!-- 对话框标题栏 -->
        <div class="fullscreen-header">
          <div class="fullscreen-title">充放电数据</div>
          <v-icon icon="mdi-close" class="close-icon" @click="closeFullscreen"></v-icon>
        </div>

        <!-- 放大的内容区域 -->
        <div class="fullscreen-body">
          <ChargeDischargeChartInner :data="data" class="fullscreen-chart-container" />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, computed, defineComponent, h } from 'vue'
import { initChart } from '@/utils'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({
      categories: [],
      tableData: [],
      incomeModel: 1,
      sharpPeriodChargeElectricEnergy: [],
      peakPeriodChargeElectricEnergy: [],
      flatPeriodChargeElectricEnergy: [],
      valleyPeriodChargeElectricEnergy: [],
      deepValleyPeriodChargeElectricEnergy: [],
      sharpPeriodDischargeElectricEnergy: [],
      peakPeriodDischargeElectricEnergy: [],
      flatPeriodDischargeElectricEnergy: [],
      valleyPeriodDischargeElectricEnergy: [],
      deepValleyPeriodDischargeElectricEnergy: [],
      sum_e_positive: [],
      sum_e_reverse: []
    })
  }
})

const chartDom = ref(null)
let chartObj = null
let resizeObserver = null

// 全屏状态
const isFullscreen = ref(false)

// 窗口尺寸
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920)
const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 1080)

const updateWindowSize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
    windowHeight.value = window.innerHeight
  }
}

// 计算对话框宽度
const dialogWidth = computed(() => {
  const maxWidth = Math.min(windowWidth.value * 0.9, 1400)
  return Math.floor(maxWidth)
})

// 计算对话框高度
const dialogHeight = computed(() => {
  const maxHeight = Math.min(windowHeight.value * 0.9, 900)
  return Math.floor(maxHeight)
})

const chartOption = computed(() => {
  const {
    categories,
    sum_e_positive = [],
    sum_e_reverse = []
  } = props.data

  // 只显示总充电和总放电
  const series = [
    {
      name: "充电",
      type: "bar",
      barWidth: 14,
      data: sum_e_positive,
      itemStyle: {
        borderRadius: [10, 10, 0, 0],
        color: '#62F7AC'
      }
    },
    {
      name: "放电",
      type: "bar",
      barWidth: 14,
      data: sum_e_reverse,
      itemStyle: {
        borderRadius: [10, 10, 0, 0],
        color: '#6295F7'
      }
    }
  ]

  return {
    grid: {
      top: 20,
      bottom: 40,
      left: 60,
      right: 20
    },
    legend: {
      show: false
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: 'rgba(255, 255, 255, 0.90)',
      borderColor: 'transparent',
      borderRadius: 16,
      extraCssText: 'backdrop-filter: blur(4px); box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);',
      textStyle: {
        color: '#2E2E2E',
        fontFamily: "HarmonyOS Sans SC",
        fontSize: 14
      },
      axisPointer: {
        type: 'line'
      },
      formatter: (params) => {
        if (!params || params.length === 0) return ''

        const date = params[0].name
        // 格式化数字，保留2位小数
        const formatNumber = (num) => {
          return Number(num || 0).toFixed(2)
        }

        // 将数据映射为 items
        const items = params.map(param => ({
          name: param.seriesName,
          value: formatNumber(param.value),
          color: param.color
        }))

        // 构建 HTML - 每行展示一个
        let html = `<div style="color: rgba(46, 46, 46, 0.70); font-family: 'HarmonyOS Sans SC'; font-size: 12px; margin-bottom: 8px;">${date}</div>`
        html += '<div style="display: flex; flex-direction: column; gap: 6px;">'

        items.forEach(item => {
          html += `<div style="display: flex; align-items: center; gap: 4px;">
            <span style="display:inline-block;margin-right:4px;border-radius:10px;width:6px;height:6px;background-color:${item.color};flex-shrink:0;"></span>
            <span style="color: #2E2E2E; font-family: 'HarmonyOS Sans SC'; font-size: 14px;">${item.name}: ${item.value} kWh</span>
          </div>`
        })

        html += '</div>'

        return html
      }
    },
    xAxis: {
      type: "category",
      data: categories,
      axisLabel: {
        fontSize: 12,
        fontFamily: 'HarmonyOS Sans SC',
        fontWeight: 300,
        color: 'rgba(46, 46, 46, 0.70)'
      },
      axisLine: {
        show: false
      }
    },
    yAxis: {
      type: "value",
      name: "电量(kWh)",
      nameTextStyle: {
        fontSize: 12,
        fontFamily: 'HarmonyOS Sans SC',
        fontWeight: 300,
        color: 'rgba(46, 46, 46, 0.70)'
      },
      axisLabel: {
        fontSize: 12,
        fontFamily: 'HarmonyOS Sans SC',
        fontWeight: 300,
        color: 'rgba(46, 46, 46, 0.70)'
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(106, 114, 124, 0.1)',
          type: 'dashed'
        }
      }
    },
    series: series
  }
})

const updateChart = () => {
  if (!chartObj) return
  chartObj.setOption(chartOption.value)
}

const initChartInstance = () => {
  if (!chartDom.value) return
  chartObj = initChart(chartDom.value)
  updateChart()

  // 监听容器大小变化
  resizeObserver = new ResizeObserver(() => {
    chartObj?.resize()
  })
  if (chartDom.value) {
    resizeObserver.observe(chartDom.value)
  }
}

// 内部图表组件 - 用于全屏显示
const ChargeDischargeChartInner = defineComponent({
  props: {
    data: {
      type: Object,
      default: () => ({
        categories: [],
        tableData: [],
        incomeModel: 1,
        sharpPeriodChargeElectricEnergy: [],
        peakPeriodChargeElectricEnergy: [],
        flatPeriodChargeElectricEnergy: [],
        valleyPeriodChargeElectricEnergy: [],
        deepValleyPeriodChargeElectricEnergy: [],
        sharpPeriodDischargeElectricEnergy: [],
        peakPeriodDischargeElectricEnergy: [],
        flatPeriodDischargeElectricEnergy: [],
        valleyPeriodDischargeElectricEnergy: [],
        deepValleyPeriodDischargeElectricEnergy: [],
        sum_e_positive: [],
        sum_e_reverse: []
      })
    }
  },
  setup(props) {
    const chartDom = ref(null)
    let chartObj = null
    let resizeObserver = null

    const chartOption = computed(() => {
      const {
        categories,
        sum_e_positive = [],
        sum_e_reverse = []
      } = props.data

      const series = [
        {
          name: "充电",
          type: "bar",
          barWidth: 14,
          data: sum_e_positive,
          itemStyle: {
            borderRadius: [10, 10, 0, 0],
            color: '#62F7AC'
          }
        },
        {
          name: "放电",
          type: "bar",
          barWidth: 14,
          data: sum_e_reverse,
          itemStyle: {
            borderRadius: [10, 10, 0, 0],
            color: '#6295F7'
          }
        }
      ]

      return {
        grid: {
          top: 20,
          bottom: 0,
          left: 60,
          right: 20
        },
        legend: {
          show: false
        },
        tooltip: {
          trigger: "axis",
          backgroundColor: 'rgba(255, 255, 255, 0.90)',
          borderColor: 'transparent',
          borderRadius: 16,
          extraCssText: 'backdrop-filter: blur(4px); box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);',
          textStyle: {
            color: '#2E2E2E',
            fontFamily: "HarmonyOS Sans SC",
            fontSize: 14
          },
          axisPointer: {
            type: 'line'
          },
          formatter: (params) => {
            if (!params || params.length === 0) return ''

            const date = params[0].name
            const formatNumber = (num) => {
              return Number(num || 0).toFixed(2)
            }

            const items = params.map(param => ({
              name: param.seriesName,
              value: formatNumber(param.value),
              color: param.color
            }))

            let html = `<div style="color: rgba(46, 46, 46, 0.70); font-family: 'HarmonyOS Sans SC'; font-size: 12px; margin-bottom: 8px;">${date}</div>`
            html += '<div style="display: flex; flex-direction: column; gap: 6px;">'

            items.forEach(item => {
              html += `<div style="display: flex; align-items: center; gap: 4px;">
                <span style="display:inline-block;margin-right:4px;border-radius:10px;width:6px;height:6px;background-color:${item.color};flex-shrink:0;"></span>
                <span style="color: #2E2E2E; font-family: 'HarmonyOS Sans SC'; font-size: 14px;">${item.name}: ${item.value} kWh</span>
              </div>`
            })

            html += '</div>'

            return html
          }
        },
        xAxis: {
          type: "category",
          data: categories,
          axisLabel: {
            fontSize: 12,
            fontFamily: 'HarmonyOS Sans SC',
            fontWeight: 300,
            color: 'rgba(46, 46, 46, 0.70)'
          },
          axisLine: {
            show: false
          }
        },
        yAxis: {
          type: "value",
          name: "电量(kWh)",
          nameTextStyle: {
            fontSize: 12,
            fontFamily: 'HarmonyOS Sans SC',
            fontWeight: 300,
            color: 'rgba(46, 46, 46, 0.70)'
          },
          axisLabel: {
            fontSize: 12,
            fontFamily: 'HarmonyOS Sans SC',
            fontWeight: 300,
            color: 'rgba(46, 46, 46, 0.70)'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(106, 114, 124, 0.1)',
              type: 'dashed'
            }
          }
        },
        series: series
      }
    })

    const initChartInstance = () => {
      if (!chartDom.value) return
      chartObj = initChart(chartDom.value)
      chartObj.setOption(chartOption.value)

      resizeObserver = new ResizeObserver(() => {
        chartObj?.resize()
      })
      if (chartDom.value) {
        resizeObserver.observe(chartDom.value)
      }
    }

    watch(() => props.data, () => {
      if (chartObj) {
        chartObj.setOption(chartOption.value)
      }
    }, { deep: true })

    watch(() => chartOption.value, () => {
      if (chartObj) {
        chartObj.setOption(chartOption.value)
      }
    })

    onMounted(() => {
      nextTick(() => {
        initChartInstance()
      })
    })

    onUnmounted(() => {
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }
      if (chartObj) {
        chartObj.dispose()
        chartObj = null
      }
    })

    return () => h('div', { ref: chartDom, class: 'chart-container' })
  }
})

// 打开全屏（暴露给父组件调用）
const openFullscreen = () => {
  isFullscreen.value = true
}

// 暴露方法给父组件
defineExpose({
  openFullscreen
})

// 关闭全屏
const closeFullscreen = () => {
  isFullscreen.value = false
}

watch(() => chartOption.value, () => {
  updateChart()
}, { deep: true })

watch(() => props.data, () => {
  updateChart()
}, { deep: true })

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateWindowSize)
    updateWindowSize()
  }
  nextTick(() => {
    initChartInstance()
  })
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWindowSize)
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (chartObj) {
    chartObj.dispose()
    chartObj = null
  }
})
</script>

<style scoped lang="scss">
.charge-discharge-chart-wrapper {
  width: 100%;
  height: 100%;
  max-height: 400px;
}

.chart-container {
  width: 100%;
  height: 100%;
}

// 全屏对话框样式
.fullscreen-dialog {
  :deep(.v-overlay__content) {
    max-width: none !important;
    max-height: none !important;
  }
}

.fullscreen-card {
  border-radius: 30px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(255, 255, 255, 1);
}

.fullscreen-header {
  position: relative;
  height: 60px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
  z-index: 10;
}

.fullscreen-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: 500;
  line-height: normal;
}

.close-icon {
  cursor: pointer;
  transition: transform 0.2s ease;
  color: #000;

  &:hover {
    transform: scale(1.1);
  }
}

.fullscreen-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
  padding: 20px;
}

.fullscreen-chart-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
</style>
